import i18n from "i18next";
import { initReactI18next } from "react-i18next";
// Only the default language (bn) is bundled into the critical chunk.
// English is loaded on demand when the visitor switches, cutting ~18KB
// of JSON parse work from initial hydration (helps desktop TBT).
import bn from "./bn.json";

export const SUPPORTED_LANGS = ["en", "bn"] as const;
export type Lang = (typeof SUPPORTED_LANGS)[number];

// IMPORTANT: initialize with a deterministic language on BOTH server and
// client. If we use `LanguageDetector` at init time, the server renders
// with "bn" (no localStorage) while the client may pick "en" — causing a
// full-tree hydration mismatch that produces the flash of blank frames.
// We always init with "bn", then swap post-hydration if the visitor has
// a saved "en" preference (see applyStoredLanguage below).
if (!i18n.isInitialized) {
  i18n
    .use(initReactI18next)
    .init({
      resources: {
        bn: { translation: bn as Record<string, unknown> },
      },
      fallbackLng: { bn: [], default: ["en"] },
      supportedLngs: SUPPORTED_LANGS as unknown as string[],
      returnEmptyString: true,
      returnNull: true,
      parseMissingKeyHandler: (key) => {
        const lng = (i18n.resolvedLanguage || i18n.language || "bn").startsWith("bn") ? "bn" : "en";
        if (lng === "bn") return "";
        return key;
      },
      interpolation: { escapeValue: false },
      lng: "bn",
    });
}

let enLoading: Promise<void> | null = null;
function ensureEnglish(): Promise<void> {
  if (i18n.hasResourceBundle("en", "translation")) return Promise.resolve();
  if (!enLoading) {
    enLoading = import("./en.json").then((m) => {
      i18n.addResourceBundle("en", "translation", (m as any).default ?? m, true, true);
    });
  }
  return enLoading;
}

// Route language changes through a wrapper that lazy-loads English on demand.
const originalChangeLanguage = i18n.changeLanguage.bind(i18n);
(i18n as any).changeLanguage = async (lng?: string, ...rest: any[]) => {
  if (lng === "en") {
    await ensureEnglish();
  }
  return originalChangeLanguage(lng as any, ...(rest as [any]));
};

/** Apply the visitor's stored language choice AFTER hydration is complete. */
export function applyStoredLanguage() {
  if (typeof window === "undefined") return;
  try {
    const stored = window.localStorage.getItem("app_lang");
    const next = stored === "en" ? "en" : "bn";
    if (i18n.language !== next) void i18n.changeLanguage(next);
  } catch {}
}

export default i18n;
